import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  doc, getDoc, updateDoc, getDocs, addDoc, collection, query, orderBy, runTransaction
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import * as XLSX from 'xlsx';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, Tabs, Tab, Select, FormControl, Tooltip, Divider,
  ToggleButtonGroup, ToggleButton, ListSubheader, Autocomplete, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArticleIcon from '@mui/icons-material/Article';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExternTidTab from '../modules/externtid/ExternTidTab';
import BudgetChart, { COLORS as CHART_COLORS } from '../components/BudgetChart';
import { useAutosave, TYPING_IDLE_MS, CHANGE_DEBOUNCE_MS } from '../hooks/useAutosave';
import { SparStatusText } from '../components/SparStatus';
import {
  diffBudgetar, applyBudgetar, sparbaraBudgetar, ofullstandigaRader,
} from '../modules/uppdrag/budgetSync';
import { harGammaltBudgetformat, kopieraManad } from '../modules/uppdrag/budgetFormat';

const STATUS_META = {
  offertfas: { label: 'Offertfas', color: '#E65100', bg: '#FFF3E0' },
  aktiv:     { label: 'Aktivt',    color: '#1B5E20', bg: '#E8F5E9' },
  pausad:    { label: 'Pausat',    color: '#4A148C', bg: '#F3E5F5' },
  avslutad:  { label: 'Avslutat',  color: '#37474F', bg: '#ECEFF1' },
};

function generateMonths(start, end) {
  if (!start || !end) return [];
  const months = [];
  let [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  while ((sy < ey || (sy === ey && sm <= em)) && months.length < 36) {
    months.push(`${sy}-${String(sm).padStart(2, '0')}`);
    sm++;
    if (sm > 12) { sm = 1; sy++; }
  }
  return months;
}

function fmtMonth(ym) {
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const [y, m] = ym.split('-');
  return names[parseInt(m, 10) - 1] + ' ' + y.slice(2);
}

const clone = v => JSON.parse(JSON.stringify(v));

function uid() {
  return Date.now().toString() + Math.random().toString(36).slice(2);
}

function fmtSEK(val) {
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

function fmtResName(res, mode) {
  // res can be a resource object { name, shortName } or just a name string (legacy)
  const name = typeof res === 'string' ? res : (res?.name || '');
  const shortName = typeof res === 'object' ? (res?.shortName || '') : '';
  if (!name && !shortName) return '(namnlös)';
  if (mode === 'first') return name.split(' ')[0] || shortName;
  if (mode === 'abbr') {
    if (shortName) return shortName;
    // Fallback: auto-generate if no shortName set
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 4).toUpperCase();
    return (parts[0].slice(0, 2) + parts[parts.length - 1].slice(0, 2)).toUpperCase();
  }
  return name; // 'full'
}

// Returns { bg, fg } for a comparison cell given budget and utfall values
function compCellStyle(budget, utfall) {
  if (!budget && !utfall) return {};
  if (!budget) return { bg: '#FFF8E1', fg: '#e65100' }; // utfall without budget
  const ratio = utfall / budget;
  if (ratio > 1.0) return { bg: '#FFEBEE', fg: '#c62828' };  // over budget
  if (ratio >= 0.8) return { bg: '#FFF8E1', fg: '#e65100' }; // 80–100 %
  return { bg: '#E8F5E9', fg: '#2e7d32' };                   // under 80 %
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState(
    searchParams.get('tab') === 'budget' ? 1
    : searchParams.get('tab') === 'externtid' ? 2
    : 0
  );
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Budget – lista av versioner
  const [budgets, setBudgets] = useState([]); // [{ id, name, startMonth, endMonth, resources, activities }]
  const [activeBudgetId, setActiveBudgetId] = useState(null);
  const [importingBudget, setImportingBudget] = useState(false);
  // Gammalt budgetformat visas skrivskyddat tills det konverterats med
  // scripts/konvertera-budgetformat.js (autosparet skriver aldrig det formatet)
  const [budgetGammaltFormat, setBudgetGammaltFormat] = useState(false);
  // Excelimport väntar på bekräftelse: { patch, ersatter: [...] }
  const [importForslag, setImportForslag] = useState(null);
  // Borttagning av rad med timmar eller belopp väntar på bekräftelse: { text, gor }
  const [bekraftaBort, setBekraftaBort] = useState(null);
  const [confirmDeleteBudgetId, setConfirmDeleteBudgetId] = useState(null);

  // Kopiera/flytta budget till annat projekt
  const [copyDialog, setCopyDialog] = useState(false);
  const [copyMode, setCopyMode] = useState('copy'); // 'copy' | 'move'
  const [copyTarget, setCopyTarget] = useState(null); // { id, label }
  const [copyProjectOptions, setCopyProjectOptions] = useState([]);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [copyDone, setCopyDone] = useState('');

  // Status dialog
  const [statusDialog, setStatusDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');

  // Projektbild
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);

  // Skapa referensprojekt
  const [createRefDialog, setCreateRefDialog] = useState(false);
  const [createRefForm, setCreateRefForm] = useState({});
  const [refTagInput, setRefTagInput] = useState('');
  const [savingRef, setSavingRef] = useState(false);

  // Visningsformat för resursnamn i aktivitetstabellen
  const [resNameMode, setResNameMode] = useState('full'); // 'first' | 'full' | 'abbr'
  // Jämförelsevy: timmar eller kronor
  const [compMode, setCompMode] = useState('hrs'); // 'hrs' | 'sek'

  // Aktivitetstabellens kolumnbredder (dragbara)
  const [actColW, setActColW] = useState(null); // null = autobredd
  const [resColW, setResColW] = useState(150);
  const dragColRef = useRef(null);
  const actColRef = useRef(null);
  const actColRenderedRef = useRef(180);
  const [actColRenderedW, setActColRenderedW] = useState(180);

  useEffect(() => {
    const el = actColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.borderBoxSize?.[0]?.inlineSize || entry.contentRect.width);
      actColRenderedRef.current = w;
      setActColRenderedW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const startColResize = useCallback((col, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = col === 'act'
      ? (actColW !== null ? actColW : actColRenderedRef.current)
      : resColW;
    dragColRef.current = { col, startX, startW };
    let slutW = null;
    const onMove = (ev) => {
      if (!dragColRef.current) return;
      const delta = ev.clientX - dragColRef.current.startX;
      const newW = Math.max(80, Math.min(400, dragColRef.current.startW + delta));
      slutW = newW;
      if (dragColRef.current.col === 'act') setActColW(newW);
      else setResColW(newW);
    };
    const onUp = () => {
      if (slutW !== null) {
        const falt = dragColRef.current?.col === 'act' ? 'actColW' : 'resColW';
        const id = activeBudgetIdRef.current;
        andraStruktur();
        setBudgets(prev => prev.map(b => (b.id === id ? { ...b, [falt]: slutW } : b)));
      }
      dragColRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [actColW, resColW]); // actColRenderedRef used via ref, not state

  const dragLicRef = useRef(null); // id of license being dragged

  // ── Rad-fill (Excel-liknande dragfyllning per resource line) ──────────────
  const fillDragRef = useRef(null);   // { actId, lineId, anchorIdx, value }
  const fillRangeRef = useRef(null);  // { actId, lineId, fromIdx, toIdx }
  const [fillHighlight, setFillHighlight] = useState(null); // { actId, lineId, fromIdx, toIdx }

  function startFillDrag(actId, lineId, anchorIdx, value, months, e) {
    e.preventDefault();
    e.stopPropagation();
    fillDragRef.current = { actId, lineId, anchorIdx, value };
    fillRangeRef.current = { actId, lineId, fromIdx: anchorIdx, toIdx: anchorIdx };
    setFillHighlight({ actId, lineId, fromIdx: anchorIdx, toIdx: anchorIdx });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'crosshair';
    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (fillDragRef.current && fillRangeRef.current) {
        const { actId: aid, lineId: lid, value: val } = fillDragRef.current;
        const { fromIdx, toIdx } = fillRangeRef.current;
        const newMonths = {};
        months.slice(fromIdx, toIdx + 1).forEach(m => { newMonths[m] = parseFloat(val) || 0; });
        updateActiveBudget(b => ({
          activities: b.activities.map(a => a.id !== aid ? a : {
            ...a,
            resourceLines: a.resourceLines.map(l => l.id !== lid ? l : {
              ...l, months: { ...l.months, ...newMonths },
            }),
          }),
        }));
      }
      fillDragRef.current = null;
      fillRangeRef.current = null;
      setFillHighlight(null);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mouseup', onUp);
  }

  function onFillCellEnter(actId, lineId, monthIdx) {
    if (!fillDragRef.current || fillDragRef.current.actId !== actId || fillDragRef.current.lineId !== lineId) return;
    const anchorIdx = fillDragRef.current.anchorIdx;
    const fromIdx = Math.min(anchorIdx, monthIdx);
    const toIdx = Math.max(anchorIdx, monthIdx);
    fillRangeRef.current = { actId, lineId, fromIdx, toIdx };
    setFillHighlight({ actId, lineId, fromIdx, toIdx });
  }

  // ── Kolumn-kopia (drag från månadsrubrik till annan) ─────────────────────
  const colCopyDragRef = useRef(null);    // { sourceIdx, months }
  const colCopyTargetRef = useRef(null);  // targetIdx
  const [colCopyState, setColCopyState] = useState(null); // { sourceIdx, targetIdx }

  function startColCopyDrag(sourceIdx, months, e) {
    e.preventDefault();
    colCopyDragRef.current = { sourceIdx, months };
    colCopyTargetRef.current = sourceIdx;
    setColCopyState({ sourceIdx, targetIdx: sourceIdx });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'copy';
    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      const tgt = colCopyTargetRef.current;
      const src = colCopyDragRef.current?.sourceIdx;
      const mo = colCopyDragRef.current?.months;
      if (mo && tgt !== undefined && tgt !== src) {
        const srcMonth = mo[src];
        const tgtMonth = mo[tgt];
        andraStruktur();
        updateActiveBudget(b => ({ activities: kopieraManad(b.activities, srcMonth, tgtMonth) }));
      }
      colCopyDragRef.current = null;
      colCopyTargetRef.current = null;
      setColCopyState(null);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mouseup', onUp);
  }

  function onColHeaderEnter(monthIdx) {
    if (!colCopyDragRef.current) return;
    colCopyTargetRef.current = monthIdx;
    setColCopyState(prev => prev ? { ...prev, targetIdx: monthIdx } : null);
  }

  const load = useCallback(async () => {
    setLoading(true);
    const [projSnap, compSnap, userSnap, deptSnap] = await Promise.all([
      getDoc(doc(db, 'projects', projectId)),
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]);
    if (projSnap.exists()) {
      const data = { id: projSnap.id, ...projSnap.data() };
      setProject(data);
      setEditForm({
        name: data.name || '',
        projectNumber: data.projectNumber || '',
        clientId: data.clientId || '',
        status: data.status || 'offertfas',
        ownerId: data.ownerId || '',
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        departmentIds: data.departmentIds || [],
        description: data.description || '',
        offerId: data.offerId || '',
        tenderId: data.tenderId || '',
      });
      // Migrate old single-budget format to budgets array
      let loadedBudgets = data.budgets || [];
      if (!loadedBudgets.length && (data.budgetResources || data.budgetActivities)) {
        loadedBudgets = [{
          id: uid(), name: 'Budget 1',
          startMonth: data.budgetStartMonth || (data.startDate ? data.startDate.slice(0, 7) : ''),
          endMonth: data.budgetEndMonth || (data.endDate ? data.endDate.slice(0, 7) : ''),
          resources: data.budgetResources || [],
          activities: data.budgetActivities || [],
        }];
      }
      // Migrate old single-resource activities to resourceLines format
      loadedBudgets = loadedBudgets.map(b => ({
        ...b,
        resources: b.resources || [],
        activities: (b.activities || []).map(a => {
          if (a.resourceLines) return a;
          return { id: a.id, name: a.name, resourceLines: [{ id: uid(), resourceId: a.resourceId || '', months: a.months || {} }] };
        }),
      }));
      setBudgets(loadedBudgets);
      const gammalt = harGammaltBudgetformat(data);
      setBudgetGammaltFormat(gammalt);
      // Ingen baslinje = autosparet skriver ingenting
      budgetBaslinjeRef.current = gammalt ? null : clone(loadedBudgets);
      setActiveBudgetId(loadedBudgets[0]?.id || null);
    }
    setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    await updateDoc(doc(db, 'projects', projectId), {
      ...editForm,
      updatedAt: new Date(),
      updatedBy: currentUser.uid,
    });
    setProject(prev => ({ ...prev, ...editForm }));
    setEditing(false);
    setSaving(false);
  }

  async function handleStatusChange(newStatus) {
    await updateDoc(doc(db, 'projects', projectId), {
      status: newStatus,
      updatedAt: new Date(),
    });
    setProject(prev => ({ ...prev, status: newStatus }));
    setEditForm(prev => ({ ...prev, status: newStatus }));
    setStatusDialog(false);
    setPendingStatus('');
  }

  function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    setImageProgress(0);
    const storageRef = ref(storage, `projects/${projectId}/image_${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => setImageProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err => { console.error(err); setImageUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await updateDoc(doc(db, 'projects', projectId), { imageUrl: url, updatedAt: new Date() });
        setProject(prev => ({ ...prev, imageUrl: url }));
        setImageUploading(false);
      }
    );
    e.target.value = '';
  }

  function openCreateRef() {
    setCreateRefForm({
      name: project.name || '',
      role: '',
      startDate: project.startDate ? project.startDate.slice(0, 7) : '',
      endDate: project.endDate ? project.endDate.slice(0, 7) : '',
      budget: '',
      projectDescription: project.description || '',
      ourAssignment: '',
      taskList: [],
      keywords: [],
      departmentId: (project.departmentIds || [])[0] || '',
      imageUrl: project.imageUrl || '',
    });
    setRefTagInput('');
    setCreateRefDialog(true);
  }

  async function handleCreateRef() {
    if (!createRefForm.name.trim() || !project.clientId) return;
    setSavingRef(true);
    await addDoc(collection(db, 'crmCompanies', project.clientId, 'references'), {
      ...createRefForm,
      sourceProjectId: projectId,
      createdAt: new Date(),
      createdBy: currentUser.uid,
    });
    await updateDoc(doc(db, 'projects', projectId), {
      isReferenceProject: true,
      updatedAt: new Date(),
    });
    setProject(prev => ({ ...prev, isReferenceProject: true }));
    setSavingRef(false);
    setCreateRefDialog(false);
  }

  async function handleToggleRef() {
    const next = !project.isReferenceProject;
    await updateDoc(doc(db, 'projects', projectId), {
      isReferenceProject: next,
      updatedAt: new Date(),
    });
    setProject(prev => ({ ...prev, isReferenceProject: next }));
  }

  // ── Budget helpers ───────────────────────────────────────────────────────

  const activeBudget = budgets.find(b => b.id === activeBudgetId) || null;
  const activeBudgetIdRef = useRef(activeBudgetId);
  activeBudgetIdRef.current = activeBudgetId;

  function updateActiveBudget(updater) {
    setBudgets(prev => prev.map(b => b.id === activeBudgetId ? { ...b, ...updater(b) } : b));
  }

  // ── Automatisk sparning (samma motor som konsultprofilen) ──
  // Ändrade fält och rader skrivs i en transaktion mot projektets aktuella
  // budgetlista, så att två som har samma budget öppen inte skriver över
  // varandras rader. Se modules/uppdrag/budgetSync.js.
  const budgetBaslinjeRef = useRef(null);  // senast sparade/lästa läget
  const budgetsRef = useRef(budgets);
  budgetsRef.current = budgets;
  // Textfält sparas efter en paus i skrivandet, val och radändringar snabbare
  const fordrojningRef = useRef(TYPING_IDLE_MS);
  function andraStruktur() { fordrojningRef.current = CHANGE_DEBOUNCE_MS; }

  function budgetDiff() {
    const bas = budgetBaslinjeRef.current;
    return diffBudgetar(bas, sparbaraBudgetar(bas, budgetsRef.current));
  }

  async function skrivBudgetar() {
    const bas = budgetBaslinjeRef.current;
    const cur = clone(sparbaraBudgetar(bas, budgetsRef.current));
    const diff = diffBudgetar(bas, cur);
    if (diff.tom) return [];
    try {
      await runTransaction(db, async tx => {
        const projRef = doc(db, 'projects', projectId);
        const snap = await tx.get(projRef);
        tx.update(projRef, {
          budgets: applyBudgetar(snap.data()?.budgets || [], diff),
          updatedAt: new Date(),
        });
      });
    } catch (err) {
      err.saveKeys = diff.keys;
      throw err;
    }
    budgetBaslinjeRef.current = cur;
    return diff.keys;
  }

  const budgetAutospar = useAutosave({
    write: skrivBudgetar,
    hasChanges: () => !budgetDiff().tom,
    ready: () => budgetBaslinjeRef.current !== null,
    logLabel: 'Budgeten',
  });

  useEffect(() => {
    if (budgetBaslinjeRef.current === null) return;
    budgetAutospar.schedule(fordrojningRef.current);
    fordrojningRef.current = TYPING_IDLE_MS;
  // schedule läser allt via refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgets]);

  /** Väntar in pågående sparning. true = allt är sparat. */
  async function sparaBudgetNu() {
    await budgetAutospar.flush();
    return budgetDiff().tom;
  }

  const ofullstandiga = ofullstandigaRader(activeBudget);
  const antalOfullstandiga = ofullstandiga.resources.length + ofullstandiga.licenses.length;

  async function bytBudget(b) {
    await sparaBudgetNu();
    setActiveBudgetId(b.id);
    setActColW(b.actColW !== undefined ? b.actColW : null);
    if (b.resColW) setResColW(b.resColW);
  }

  async function bytFlik(v) {
    if (tab === 1 && v !== 1) await sparaBudgetNu();
    setTab(v);
  }

  async function oppnaRapport() {
    if (!(await sparaBudgetNu())) return; // felet syns i statusraden
    navigate(`/projects/${projectId}/budget-report?budgetId=${activeBudgetId}`);
  }

  /** Bekräftelse innan en rad med timmar eller belopp tas bort. */
  function taBortMedBekraftelse(harInnehall, text, gor) {
    if (harInnehall) setBekraftaBort({ text, gor });
    else gor();
  }

  async function handleAddBudget() {
    await sparaBudgetNu();
    andraStruktur();
    const newB = {
      id: uid(),
      name: `Budget ${budgets.length + 1}`,
      startMonth: project.startDate ? project.startDate.slice(0, 7) : '',
      endMonth: project.endDate ? project.endDate.slice(0, 7) : '',
      resources: [],
      activities: [],
    };
    setBudgets(prev => [...prev, newB]);
    setActiveBudgetId(newB.id);
  }

  function handleDeleteBudget(id) {
    setConfirmDeleteBudgetId(id);
  }

  function confirmDeleteBudget() {
    const id = confirmDeleteBudgetId;
    setConfirmDeleteBudgetId(null);
    const remaining = budgets.filter(b => b.id !== id);
    andraStruktur();
    setBudgets(remaining);
    if (activeBudgetId === id) setActiveBudgetId(remaining[0]?.id || null);
  }

  // Deep-clone a budget with fresh IDs (resources, activities, lines, licenses).
  function cloneBudget(src, name) {
    const resourceIdMap = {};
    const newResources = (src.resources || []).map(r => {
      const newId = uid();
      resourceIdMap[r.id] = newId;
      return { ...r, id: newId };
    });
    const newActivities = (src.activities || []).map(a => ({
      ...a,
      id: uid(),
      resourceLines: (a.resourceLines || []).map(l => ({
        ...l,
        id: uid(),
        resourceId: resourceIdMap[l.resourceId] || l.resourceId,
        months: { ...l.months },
      })),
    }));
    return {
      id: uid(),
      name: name ?? src.name,
      startMonth: src.startMonth,
      endMonth: src.endMonth,
      resources: newResources,
      activities: newActivities,
      licenses: (src.licenses || []).map(l => ({ ...l, id: uid() })),
      ...(src.actColW !== undefined ? { actColW: src.actColW } : {}),
      ...(src.resColW !== undefined ? { resColW: src.resColW } : {}),
    };
  }

  async function handleDuplicateBudget() {
    if (!activeBudget) return;
    await sparaBudgetNu();
    andraStruktur();
    const newB = cloneBudget(activeBudget, `Kopia av ${activeBudget.name}`);
    setBudgets(prev => [...prev, newB]);
    setActiveBudgetId(newB.id);
  }

  async function openCopyBudget() {
    if (!activeBudget) return;
    await sparaBudgetNu();
    setCopyMode('copy');
    setCopyTarget(null);
    setCopyError('');
    setCopyDone('');
    setCopyDialog(true);
    // Load other projects for the picker
    try {
      const snap = await getDocs(query(collection(db, 'projects'), orderBy('name')));
      const opts = snap.docs
        .filter(d => d.id !== projectId)
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            label: `${data.projectNumber ? data.projectNumber + ' – ' : ''}${data.name || '(namnlöst)'}`,
          };
        });
      setCopyProjectOptions(opts);
    } catch (err) {
      setCopyError('Kunde inte ladda projektlistan.');
    }
  }

  async function handleCopyBudgetToProject() {
    if (!activeBudget || !copyTarget) return;
    setCopyBusy(true);
    setCopyError('');
    try {
      // Read target project's current budgets
      const targetRef = doc(db, 'projects', copyTarget.id);
      const targetSnap = await getDoc(targetRef);
      if (!targetSnap.exists()) {
        setCopyError('Målprojektet kunde inte hittas.');
        setCopyBusy(false);
        return;
      }
      const targetBudgets = targetSnap.data().budgets || [];
      const cloned = cloneBudget(budgetBaslinjeRef.current.find(b => b.id === activeBudget.id) || activeBudget); // sparat läge, samma namn
      await updateDoc(targetRef, {
        budgets: [...targetBudgets, cloned],
        updatedAt: new Date(),
      });

      if (copyMode === 'move') {
        // Tas bort ur det här projektet av autosparet (bara den budgeten)
        const remaining = budgets.filter(b => b.id !== activeBudgetId);
        fordrojningRef.current = 0;
        setBudgets(remaining);
        if (activeBudgetId === activeBudget.id) setActiveBudgetId(remaining[0]?.id || null);
      }

      setCopyDone(
        copyMode === 'move'
          ? `Budgeten flyttades till "${copyTarget.label}".`
          : `Budgeten kopierades till "${copyTarget.label}".`
      );
    } catch (err) {
      setCopyError('Något gick fel när budgeten skulle överföras.');
    } finally {
      setCopyBusy(false);
    }
  }

  function handleBudgetNameChange(val) {
    updateActiveBudget(() => ({ name: val }));
  }

  function handleBudgetPeriodChange(field, val) {
    updateActiveBudget(() => ({ [field]: val }));
  }

  function handleDeriveMonthsFromProject() {
    andraStruktur();
    updateActiveBudget(() => ({
      startMonth: project.startDate ? project.startDate.slice(0, 7) : '',
      endMonth: project.endDate ? project.endDate.slice(0, 7) : '',
    }));
  }

  const [newResourceUserId, setNewResourceUserId] = useState('');
  const [newRoleForm, setNewRoleForm] = useState({ open: false, name: '', hourlyRate: '' });

  function handleAddRole() {
    if (!newRoleForm.name.trim()) return;
    andraStruktur();
    updateActiveBudget(b => ({
      resources: [...(b.resources || []), {
        id: uid(), name: newRoleForm.name.trim(),
        shortName: '', hourlyRate: newRoleForm.hourlyRate, isRole: true,
      }],
    }));
    setNewRoleForm({ open: false, name: '', hourlyRate: '' });
  }

  function handleAddResource(userId) {
    andraStruktur();
    if (userId) {
      const user = users.find(u => u.id === userId);
      updateActiveBudget(b => ({
        resources: [...(b.resources || []), {
          id: uid(),
          userId,
          name: user?.displayName || user?.name || user?.email || '',
          shortName: user?.shortName || '',
          hourlyRate: '',
        }],
      }));
      setNewResourceUserId('');
    } else {
      updateActiveBudget(b => ({ resources: [...(b.resources || []), { id: uid(), name: '', shortName: '', hourlyRate: '' }] }));
    }
  }
  function handleResourceChange(id, field, value) {
    updateActiveBudget(b => ({ resources: (b.resources || []).map(r => r.id === id ? { ...r, [field]: value } : r) }));
  }
  function handleDeleteResource(id) {
    const res = activeBudget?.resources?.find(r => r.id === id);
    const timmar = (activeBudget?.activities || []).some(a => (a.resourceLines || [])
      .some(l => l.resourceId === id && Object.values(l.months || {}).some(v => parseFloat(v) > 0)));
    taBortMedBekraftelse(timmar || parseFloat(res?.hourlyRate) > 0,
      `Ta bort ${res?.name || 'resursen'}? ${timmar ? 'Timmarna i aktiviteterna blir kvar utan person.' : 'Timpriset försvinner.'}`,
      () => taBortResurs(id));
  }
  function taBortResurs(id) {
    andraStruktur();
    updateActiveBudget(b => ({
      resources: (b.resources || []).filter(r => r.id !== id),
      activities: b.activities.flatMap(a => {
        const remaining = (a.resourceLines || []).map(l =>
          l.resourceId === id ? { ...l, resourceId: '' } : l
        );
        return [{ ...a, resourceLines: remaining }];
      }),
    }));
  }

  function handleAddActivity(months) {
    andraStruktur();
    const mo = {};
    months.forEach(m => { mo[m] = 0; });
    const line = { id: uid(), resourceId: '', months: mo };
    updateActiveBudget(b => ({ activities: [...b.activities, { id: uid(), name: '', resourceLines: [line] }] }));
  }
  function handleActivityChange(id, field, value) {
    updateActiveBudget(b => ({ activities: b.activities.map(a => a.id === id ? { ...a, [field]: value } : a) }));
  }
  function handleResourceLineMonthChange(actId, lineId, month, value) {
    updateActiveBudget(b => ({
      activities: b.activities.map(a => a.id !== actId ? a : {
        ...a,
        resourceLines: a.resourceLines.map(l => l.id !== lineId ? l : {
          ...l, months: { ...l.months, [month]: parseFloat(value) || 0 },
        }),
      }),
    }));
  }
  function handleResourceLineChange(actId, lineId, field, value) {
    andraStruktur();
    updateActiveBudget(b => ({
      activities: b.activities.map(a => a.id !== actId ? a : {
        ...a,
        resourceLines: a.resourceLines.map(l => l.id !== lineId ? l : { ...l, [field]: value }),
      }),
    }));
  }
  function handleAddResourceLine(actId, months) {
    andraStruktur();
    const mo = {};
    months.forEach(m => { mo[m] = 0; });
    updateActiveBudget(b => ({
      activities: b.activities.map(a => a.id !== actId ? a : {
        ...a,
        resourceLines: [...a.resourceLines, { id: uid(), resourceId: '', months: mo }],
      }),
    }));
  }
  function handleDeleteResourceLine(actId, lineId) {
    const line = activeBudget?.activities?.find(a => a.id === actId)?.resourceLines?.find(l => l.id === lineId);
    const timmar = Object.values(line?.months || {}).reduce((t, v) => t + (parseFloat(v) || 0), 0);
    taBortMedBekraftelse(timmar > 0, `Ta bort raden med ${timmar} timmar?`,
      () => taBortAktivitetsrad(actId, lineId));
  }
  function taBortAktivitetsrad(actId, lineId) {
    andraStruktur();
    updateActiveBudget(b => ({
      activities: b.activities.flatMap(a => {
        if (a.id !== actId) return [a];
        const remaining = a.resourceLines.filter(l => l.id !== lineId);
        return remaining.length > 0 ? [{ ...a, resourceLines: remaining }] : [];
      }),
    }));
  }
  // License handlers
  function handleAddLicense() {
    andraStruktur();
    updateActiveBudget(b => ({
      licenses: [...(b.licenses || []), { id: uid(), name: '', priceType: 'monthly', price: '', quantity: 1 }],
    }));
  }
  function handleLicenseChange(id, field, value) {
    updateActiveBudget(b => ({
      licenses: (b.licenses || []).map(l => l.id === id ? { ...l, [field]: value } : l),
    }));
  }
  function handleDeleteLicense(id) {
    const lic = activeBudget?.licenses?.find(l => l.id === id);
    taBortMedBekraftelse(parseFloat(lic?.price) > 0, `Ta bort licensen ${lic?.name || ''} (${lic?.price} kr)?`, () => {
      andraStruktur();
      updateActiveBudget(b => ({ licenses: (b.licenses || []).filter(l => l.id !== id) }));
    });
  }

  async function handleImportTikabBudget(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImportingBudget(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = wb.Sheets['Planering'];
      if (!sheet) { setImportingBudget(false); return; }
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const svMonths = { jan:1, feb:2, mar:3, apr:4, maj:5, jun:6, jul:7, aug:8, sep:9, okt:10, nov:11, dec:12 };

      let monthHeaderRow = -1;
      let resourceHeaderRow = -1;
      for (let i = 0; i < rows.length; i++) {
        if (String(rows[i][0] || '').includes('Aktiviteter') && String(rows[i][1] || '').includes('Roller')) {
          monthHeaderRow = i;
        }
        if (String(rows[i][0] || '').includes('Roller / Pers') ||
            (String(rows[i][1] || '').toLowerCase().includes('timarvode'))) {
          resourceHeaderRow = i;
        }
      }

      const importedMonths = [];
      if (monthHeaderRow >= 0) {
        for (let c = 3; c < (rows[monthHeaderRow] || []).length; c++) {
          const cell = String(rows[monthHeaderRow][c] || '').toLowerCase().trim();
          const parts = cell.split(' ');
          if (parts.length === 2) {
            const mn = svMonths[parts[0]];
            const yr = parts[1].length === 4 ? parts[1] : '20' + parts[1];
            if (mn && yr) importedMonths.push(`${yr}-${String(mn).padStart(2,'0')}`);
          }
        }
      }

      const importedResources = [];
      if (resourceHeaderRow >= 0 && monthHeaderRow > resourceHeaderRow) {
        for (let i = resourceHeaderRow + 1; i < monthHeaderRow; i++) {
          const r = rows[i];
          if (!r || !r[0] || typeof r[0] !== 'string') continue;
          const name = String(r[0]).trim();
          const rate = parseFloat(r[1]) || 0;
          if (name && rate) {
            importedResources.push({ id: uid(), name, hourlyRate: rate });
          }
        }
      }

      const importedActivities = [];
      if (monthHeaderRow >= 0) {
        for (let i = monthHeaderRow + 1; i < rows.length; i++) {
          const r = rows[i];
          if (!r || !r[0]) continue;
          const actName = String(r[0]).trim();
          const personName = String(r[1] || '').trim();
          if (!actName) continue;
          const resource = importedResources.find(res => res.name === personName);
          const mo = {};
          importedMonths.forEach((m, idx) => {
            mo[m] = parseFloat(r[3 + idx]) || 0;
          });
          importedActivities.push({
            id: uid(),
            name: actName,
            resourceLines: [{ id: uid(), resourceId: resource?.id || '', months: mo }],
          });
        }
      }

      const patch = {
        ...(importedMonths.length > 0 ? {
          startMonth: importedMonths[0],
          endMonth: importedMonths[importedMonths.length - 1],
        } : {}),
        ...(importedResources.length > 0 ? { resources: importedResources } : {}),
        ...(importedActivities.length > 0 ? { activities: importedActivities } : {}),
      };
      // Det finns ingen Spara-knapp att avstå från – visa vad som ersätts först
      const ersatter = [];
      if (patch.startMonth) ersatter.push(`Perioden ${activeBudget.startMonth || '–'} – ${activeBudget.endMonth || '–'} ersätts med ${patch.startMonth} – ${patch.endMonth}`);
      if (patch.resources) ersatter.push(`${(activeBudget.resources || []).length} resurser ersätts med ${patch.resources.length}`);
      if (patch.activities) ersatter.push(`${(activeBudget.activities || []).length} aktiviteter ersätts med ${patch.activities.length}`);
      if (ersatter.length) setImportForslag({ patch, ersatter, fil: file.name });
    } catch (err) {
      console.error('Fel vid budgetimport:', err);
    }
    setImportingBudget(false);
  }

  function handleUtfallImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const sheet = wb.Sheets['ProjectTimeReport'] || wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        // Same column layout as handleMilltimeImport: col2=Datum, col3=Namn, col7=Antal
        const byNameMonth = {};
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || !row[3]) continue;
          const name = String(row[3]).trim();
          const hours = parseFloat(row[7]) || 0;
          if (!name || !hours) continue;
          // Parse date → YYYY-MM
          let month = '';
          const rawDate = row[2];
          if (typeof rawDate === 'number') {
            const d = XLSX.SSF.parse_date_code(rawDate);
            if (d) month = `${d.y}-${String(d.m).padStart(2, '0')}`;
          } else {
            const ds = String(rawDate || '');
            if (ds.match(/^\d{4}-\d{2}/)) month = ds.slice(0, 7);
            else if (ds.match(/^\d{2}\/\d{2}\/\d{4}/)) {
              const [, mm, yyyy] = ds.split('/');
              month = `${yyyy}-${mm}`;
            }
          }
          if (!month) continue;
          if (!byNameMonth[name]) byNameMonth[name] = {};
          byNameMonth[name][month] = (byNameMonth[name][month] || 0) + hours;
        }
        // Map names → resourceId in active budget
        const res = activeBudget?.resources || [];
        const newUtfall = { ...(activeBudget?.utfall || {}) };
        for (const [name, monthHours] of Object.entries(byNameMonth)) {
          const r = res.find(r2 =>
            (r2.name || '').toLowerCase().trim() === name.toLowerCase() ||
            (r2.name || '').toLowerCase().startsWith(name.toLowerCase())
          );
          if (!r) continue;
          newUtfall[r.id] = { ...(newUtfall[r.id] || {}) };
          for (const [m, h] of Object.entries(monthHours)) {
            newUtfall[r.id][m] = h; // overwrite (not accumulate)
          }
        }
        updateActiveBudget(() => ({ utfall: newUtfall }));
      } catch (err) {
        console.error('Utfall import error:', err);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!project) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">Uppdraget hittades inte.</Typography>
    </Box>
  );

  const st = STATUS_META[project.status] || STATUS_META.offertfas;
  const clientCompany = companies.find(c => c.id === project.clientId);
  const ownerUser = users.find(u => u.id === project.ownerId);
  const projDepts = departments.filter(d => (project.departmentIds || []).includes(d.id));

  // Active budget derived values
  const budgetResources = activeBudget?.resources || [];
  const budgetActivities = activeBudget?.activities || [];
  const budgetLicenses = activeBudget?.licenses || [];
  const budgetMonths = generateMonths(activeBudget?.startMonth || '', activeBudget?.endMonth || '');

  // Monthly comparison: budget hrs per resource per month (from all resourceLines)
  const budgHrsByResMonth = {};
  budgetResources.forEach(res => {
    budgHrsByResMonth[res.id] = {};
    budgetMonths.forEach(m => {
      budgHrsByResMonth[res.id][m] = budgetActivities
        .flatMap(a => (a.resourceLines || []).filter(l => l.resourceId === res.id))
        .reduce((s, l) => s + (parseFloat(l.months?.[m]) || 0), 0);
    });
  });

  // License total cost given budget period
  function licenseBudgetTotal(lic) {
    const p = parseFloat(lic.price) || 0;
    if (lic.priceType === 'fixed') return p;
    const q = parseFloat(lic.quantity) || 1;
    return p * q;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/projects')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">{project.name}</Typography>
              <Chip label={st.label} size="small"
                sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600,
                  fontSize: '0.65rem', height: 20 }} />
            </Box>
            {project.projectNumber && (
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                {project.projectNumber}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!editing ? (
            <Button variant="outlined" startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>
              Redigera
            </Button>
          ) : (
            <>
              <Button onClick={() => setEditing(false)}
                sx={{ color: 'white' }}>
                Avbryt
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />}
                onClick={handleSave} disabled={saving}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                {saving ? 'Sparar...' : 'Spara'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Tabs value={tab} onChange={(_, v) => bytFlik(v)} sx={{ mb: 1 }}>
          <Tab label="Översikt" />
          <Tab label="Budget" />
          <Tab label="ExternTid" />
        </Tabs>

        {/* ── Tab 0: Översikt ── */}
        <TabPanel value={tab} index={0}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Projektnamn
                </Typography>
                {editing ? (
                  <TextField fullWidth size="small" sx={{ mt: 0.5 }}
                    value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                ) : (
                  <Typography>{project.name}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Projektnummer
                </Typography>
                {editing ? (
                  <TextField fullWidth size="small" sx={{ mt: 0.5 }}
                    value={editForm.projectNumber}
                    onChange={e => setEditForm(f => ({ ...f, projectNumber: e.target.value }))} />
                ) : (
                  <Typography>{project.projectNumber || '–'}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Kund
                </Typography>
                {editing ? (
                  <TextField select fullWidth size="small" sx={{ mt: 0.5 }}
                    value={editForm.clientId}
                    onChange={e => setEditForm(f => ({ ...f, clientId: e.target.value }))}>
                    <MenuItem value="">– Ingen –</MenuItem>
                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </TextField>
                ) : (
                  <Typography>{clientCompany?.name || '–'}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Status
                </Typography>
                {editing ? (
                  <TextField select fullWidth size="small" sx={{ mt: 0.5 }}
                    value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <MenuItem key={k} value={k}>{v.label}</MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={st.label} size="small"
                      sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600 }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Ägare
                </Typography>
                {editing ? (
                  <TextField select fullWidth size="small" sx={{ mt: 0.5 }}
                    value={editForm.ownerId}
                    onChange={e => setEditForm(f => ({ ...f, ownerId: e.target.value }))}>
                    <MenuItem value="">– Ingen –</MenuItem>
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.displayName || u.email}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Typography>{ownerUser?.displayName || ownerUser?.email || '–'}</Typography>
                )}
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Startdatum
                </Typography>
                {editing ? (
                  <TextField fullWidth size="small" type="date"
                    slotProps={{ inputLabel: { shrink: true } }} sx={{ mt: 0.5 }}
                    value={editForm.startDate}
                    onChange={e => setEditForm(f => ({ ...f, startDate: e.target.value }))} />
                ) : (
                  <Typography>{project.startDate || '–'}</Typography>
                )}
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Slutdatum
                </Typography>
                {editing ? (
                  <TextField fullWidth size="small" type="date"
                    slotProps={{ inputLabel: { shrink: true } }} sx={{ mt: 0.5 }}
                    value={editForm.endDate}
                    onChange={e => setEditForm(f => ({ ...f, endDate: e.target.value }))} />
                ) : (
                  <Typography>{project.endDate || '–'}</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Affärsområden
                </Typography>
                {editing ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                    {departments.map(d => {
                      const sel = (editForm.departmentIds || []).includes(d.id);
                      return (
                        <Tooltip key={d.id} title={d.name}>
                    <Chip label={deptCode(d)} size="small"
                          onClick={() => setEditForm(f => ({
                            ...f,
                            departmentIds: sel
                              ? f.departmentIds.filter(id => id !== d.id)
                              : [...(f.departmentIds || []), d.id]
                          }))}
                          variant={sel ? 'filled' : 'outlined'}
                          sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                            color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {projDepts.length > 0
                      ? projDepts.map(d => (
                          <Tooltip key={d.id} title={d.name}>
                            <Chip label={deptCode(d)} size="small"
                              sx={{ bgcolor: 'text.primary', color: 'background.paper', fontSize: '0.65rem' }} />
                          </Tooltip>
                        ))
                      : <Typography color="text.secondary">–</Typography>
                    }
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Beskrivning
                </Typography>
                {editing ? (
                  <TextField fullWidth size="small" multiline rows={3} sx={{ mt: 0.5 }}
                    value={editForm.description}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.description || '–'}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Projektbild */}
            <Grid item xs={12}>
              <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                  Projektbild
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {project.imageUrl ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box component="img" src={project.imageUrl} alt="Projektbild"
                        sx={{ width: 180, height: 120, objectFit: 'cover',
                          borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                    </Box>
                  ) : (
                    <Box sx={{ width: 180, height: 120, bgcolor: 'action.hover', borderRadius: 1,
                      border: '2px dashed', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.disabled">Ingen bild</Typography>
                    </Box>
                  )}
                  <Box>
                    <Button component="label" variant="outlined" size="small"
                      startIcon={imageUploading
                        ? <CircularProgress size={14} />
                        : <UploadFileIcon sx={{ fontSize: 16 }} />}
                      disabled={imageUploading}>
                      {imageUploading ? `${imageProgress}%` : project.imageUrl ? 'Byt bild' : 'Ladda upp bild'}
                      <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      JPG, PNG eller WebP. Visas automatiskt<br />vid skapande av referensprojekt.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Länkade objekt */}
            {(project.offerId || project.tenderId) && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Kopplat till
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {project.offerId && (
                    <Chip label="Offert" size="small" onClick={() => navigate(`/offers/${project.offerId}`)}
                      sx={{ cursor: 'pointer', bgcolor: '#E3F2FD', color: '#1565C0' }} />
                  )}
                  {project.tenderId && (
                    <Chip label="Anbud" size="small" onClick={() => navigate(`/tenders/${project.tenderId}/team`)}
                      sx={{ cursor: 'pointer', bgcolor: '#FFF3E0', color: '#E65100' }} />
                  )}
                </Box>
              </Box>
            )}

            {/* Statusknapp + Skapa referensprojekt */}
            {!editing && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider',
                display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {project.status === 'offertfas' && (
                  <Button variant="outlined" size="small"
                    sx={{ color: '#1B5E20', borderColor: '#1B5E20' }}
                    onClick={() => { setPendingStatus('aktiv'); setStatusDialog(true); }}>
                    Markera som aktivt
                  </Button>
                )}
                {project.status === 'aktiv' && (
                  <Button variant="outlined" size="small"
                    sx={{ color: '#37474F', borderColor: '#37474F' }}
                    onClick={() => { setPendingStatus('avslutad'); setStatusDialog(true); }}>
                    Markera som avslutat
                  </Button>
                )}
                <Button
                  variant={project.isReferenceProject ? 'contained' : 'outlined'}
                  size="small" startIcon={<StarIcon />}
                  onClick={handleToggleRef}
                  sx={project.isReferenceProject
                    ? { bgcolor: '#FF6B00', color: 'white', '&:hover': { bgcolor: '#e55a00' } }
                    : { color: '#FF6B00', borderColor: '#FF6B00', '&:hover': { bgcolor: '#FFF3E0' } }}>
                  {project.isReferenceProject ? 'Referensuppdrag ✓' : 'Markera som referensuppdrag'}
                </Button>
                <Button variant="outlined" size="small" startIcon={<StarIcon />}
                  disabled={!project.clientId}
                  title={!project.clientId ? 'Koppla en kund till uppdraget för att skapa referensprojekt' : ''}
                  onClick={openCreateRef}>
                  Skapa referensprojekt
                </Button>
              </Box>
            )}
          </Paper>
        </TabPanel>

        {/* ── Tab 1: Budget ── */}
        <TabPanel value={tab} index={1}>
          {budgetGammaltFormat && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Budgeten har ett äldre format och visas skrivskyddad. Konverteras – kontakta Johan.
            </Alert>
          )}
          <Box onBlur={() => budgetAutospar.flush()} inert={budgetGammaltFormat || undefined}
            sx={budgetGammaltFormat ? { opacity: 0.75 } : undefined}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight="bold">Budget</Typography>
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.85rem',
                color: budgetAutospar.failing ? 'error.main' : 'text.secondary' }}>
                <SparStatusText autosave={budgetAutospar} readOnly={budgetGammaltFormat}
                  readOnlyText="Skrivskyddad – äldre format" />
                {antalOfullstandiga > 0 && !budgetAutospar.failing && (
                  <Box component="span" sx={{ color: 'status.naraText' }}>
                    · {antalOfullstandiga} ofullständig{antalOfullstandiga > 1 ? 'a rader sparas' : ' rad sparas'} inte än
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={importingBudget ? <CircularProgress size={14} /> : <UploadFileIcon />}
                disabled={importingBudget || !activeBudget}>
                Importera Tikab Excel
                <input type="file" hidden accept=".xls,.xlsx" onChange={handleImportTikabBudget} />
              </Button>
              <Button size="small" variant="outlined" startIcon={<ArticleIcon />}
                disabled={!activeBudget}
                onClick={oppnaRapport}>
                Rapport
              </Button>
            </Box>
          </Box>

          {/* Budget-väljare */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {budgets.map(b => (
              <Chip
                key={b.id}
                label={b.name}
                onClick={() => bytBudget(b)}
                onDelete={budgets.length > 1 ? () => handleDeleteBudget(b.id) : undefined}
                variant={activeBudgetId === b.id ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: activeBudgetId === b.id ? 'text.primary' : 'transparent',
                  color: activeBudgetId === b.id ? 'background.paper' : 'text.primary',
                  fontWeight: activeBudgetId === b.id ? 'bold' : 'normal',
                  cursor: 'pointer',
                  '& .MuiChip-deleteIcon': {
                    color: activeBudgetId === b.id ? 'background.paper' : undefined, opacity: activeBudgetId === b.id ? 0.7 : undefined,
                    '&:hover': { color: activeBudgetId === b.id ? 'background.paper' : 'error.main', opacity: 1 },
                  },
                }}
              />
            ))}
            <Chip
              icon={<AddIcon sx={{ fontSize: 16 }} />}
              label="Ny budget"
              variant="outlined"
              onClick={handleAddBudget}
              sx={{ cursor: 'pointer', borderStyle: 'dashed' }}
            />
            {activeBudget && (
              <Chip
                icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                label="Duplicera"
                variant="outlined"
                onClick={handleDuplicateBudget}
                sx={{ cursor: 'pointer', borderStyle: 'dashed' }}
              />
            )}
            {activeBudget && (
              <Chip
                icon={<DriveFileMoveOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Kopiera/flytta till projekt"
                variant="outlined"
                onClick={openCopyBudget}
                sx={{ cursor: 'pointer', borderStyle: 'dashed' }}
              />
            )}
          </Box>

          {!activeBudget ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Skapa en budget med knappen ovan för att börja planera.
              </Typography>
            </Paper>
          ) : (<>

          {/* Period + Namn */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Budgetnamn"
                value={activeBudget.name}
                onChange={e => handleBudgetNameChange(e.target.value)}
                sx={{ flex: '1 1 160px', maxWidth: 220 }}
              />
              <Box sx={{ width: 1, borderLeft: '1px solid', borderColor: 'divider', height: 32, mx: 0.5 }} />
              <Typography variant="body2" fontWeight="bold">Period:</Typography>
              <TextField
                size="small"
                type="month"
                label="Start"
                value={activeBudget.startMonth}
                onChange={e => handleBudgetPeriodChange('startMonth', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: '1 1 190px', minWidth: 190, maxWidth: 230 }}
              />
              <Typography variant="body2">–</Typography>
              <TextField
                size="small"
                type="month"
                label="Slut"
                value={activeBudget.endMonth}
                onChange={e => handleBudgetPeriodChange('endMonth', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: '1 1 190px', minWidth: 190, maxWidth: 230 }}
              />
              <Button size="small" variant="outlined" onClick={handleDeriveMonthsFromProject}>
                Hämta från projektdatum
              </Button>
            </Box>
          </Paper>

          {/* Resurser */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight="bold">Resurser</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* User picker — filtered to project's departments */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={newResourceUserId}
                    onChange={e => setNewResourceUserId(e.target.value)}
                    displayEmpty
                    renderValue={val => {
                      if (!val) return <Box component="span" sx={{ color: 'text.disabled' }}>Välj medarbetare…</Box>;
                      const u = users.find(u => u.id === val);
                      return u?.displayName || u?.name || u?.email || val;
                    }}>
                    <MenuItem value=""><em>– Välj medarbetare –</em></MenuItem>
                    {(() => {
                      const projectDeptIds = project?.departmentIds || [];
                      // Prefer project departments; fall back to all departments if none have available users
                      const preferred = departments.filter(d => projectDeptIds.includes(d.id));
                      const hasAvailable = preferred.some(d =>
                        users.some(u => u.departmentId === d.id && !budgetResources.some(r => r.userId === u.id)));
                      const activeDepts = (preferred.length > 0 && hasAvailable) ? preferred : departments;
                      // NOTE: MUI Select requires its children to be a flat list of
                      // ListSubheader/MenuItem elements — it clones each child to inject
                      // the click handler. Wrapping items in <Fragment> breaks selection,
                      // so we flatMap to a flat keyed array instead.
                      return activeDepts.flatMap(dept => {
                        const deptUsers = users.filter(u => u.departmentId === dept.id
                          && !budgetResources.some(r => r.userId === u.id));
                        if (!deptUsers.length) return [];
                        return [
                          <ListSubheader key={`h-${dept.id}`} sx={{ fontSize: '0.7rem', lineHeight: '28px', bgcolor: 'yta.rubrik' }}>
                            {dept.offerCode ? `${dept.offerCode} – ${dept.name}` : dept.name}
                          </ListSubheader>,
                          ...deptUsers.map(u => (
                            <MenuItem key={u.id} value={u.id} sx={{ pl: 3 }}>
                              <Box>
                                <Typography sx={{ fontSize: '0.875rem' }}>
                                  {u.displayName || u.name || u.email}
                                  {u.shortName && <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 0.75 }}>{u.shortName}</Box>}
                                </Typography>
                                {u.title && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{u.title}</Typography>}
                              </Box>
                            </MenuItem>
                          )),
                        ];
                      });
                    })()}
                  </Select>
                </FormControl>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => handleAddResource(newResourceUserId)}
                  disabled={!newResourceUserId}
                  sx={{ whiteSpace: 'nowrap' }}>
                  Lägg till
                </Button>
                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                  onClick={() => setNewRoleForm(f => ({ ...f, open: !f.open }))}
                  sx={{ whiteSpace: 'nowrap' }}>
                  Lägg till roll
                </Button>
              </Box>
            </Box>
            {/* Inline roll-form */}
            {newRoleForm.open && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, p: 1.5,
                bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <TextField size="small" label="Rollnamn" value={newRoleForm.name}
                  onChange={e => setNewRoleForm(f => ({ ...f, name: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleAddRole()}
                  placeholder="t.ex. BIM-samordnare" sx={{ flex: 1 }} autoFocus />
                <TextField size="small" label="Timpris (kr/tim)" value={newRoleForm.hourlyRate}
                  onChange={e => setNewRoleForm(f => ({ ...f, hourlyRate: e.target.value.replace(/[^0-9]/g, '') }))}
                  onKeyDown={e => e.key === 'Enter' && handleAddRole()}
                  sx={{ width: 150 }} />
                <Button size="small" variant="contained" onClick={handleAddRole}
                  disabled={!newRoleForm.name.trim()}>
                  Lägg till
                </Button>
                <Tooltip title="Rollbaserad resurs kan inte matchas mot Milltime-utfall. Du kan byta till en specifik person senare." arrow>
                  <WarningAmberIcon sx={{ fontSize: 18, color: '#E65100' }} />
                </Tooltip>
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'yta.rubrik' } }}>
                  <TableCell>Person / Roll</TableCell>
                  <TableCell align="right">Timpris (kr/tim)</TableCell>
                  <TableCell align="right">Totalt timmar</TableCell>
                  <TableCell align="right">Totalt värde</TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {budgetResources.map((res, resIdx) => {
                  const totalHrs = budgetActivities.reduce((s, act) =>
                    s + (act.resourceLines || [])
                      .filter(l => l.resourceId === res.id)
                      .reduce((ls, l) => ls + budgetMonths.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0), 0), 0);
                  const totalVal = totalHrs * (parseFloat(res.hourlyRate) || 0);
                  const autoColor = CHART_COLORS[resIdx % CHART_COLORS.length];
                  return (
                    <TableRow key={res.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <Tooltip title="Klicka för att ändra färg i grafen" arrow>
                              <Box
                                sx={{
                                  width: 13, height: 13, borderRadius: '50%', cursor: 'pointer',
                                  bgcolor: res.color || autoColor,
                                  border: '1.5px solid', borderColor: 'divider',
                                  transition: 'transform 0.1s, border-color 0.1s',
                                  '&:hover': { transform: 'scale(1.25)', borderColor: 'text.secondary' },
                                }}
                                onClick={() => document.getElementById(`clr-${res.id}`)?.click()}
                              />
                            </Tooltip>
                            <input
                              id={`clr-${res.id}`}
                              type="color"
                              value={res.color || autoColor}
                              onChange={e => handleResourceChange(res.id, 'color', e.target.value)}
                              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, top: 0, left: 0, pointerEvents: 'none' }}
                            />
                          </Box>
                          {res.isRole ? (
                            <Tooltip title="Rollbaserad resurs — kan inte matchas mot Milltime-utfall. Ersätt med en namngiven person för att aktivera utfallsuppföljning." arrow>
                              <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100', flexShrink: 0 }} />
                            </Tooltip>
                          ) : null}
                          <Box>
                            {res.isRole ? (
                              <TextField variant="standard" size="small" value={res.name}
                                onChange={e => handleResourceChange(res.id, 'name', e.target.value)}
                                error={ofullstandiga.resources.includes(res.id)}
                                helperText={ofullstandiga.resources.includes(res.id) ? 'Sparas när rollen har ett namn' : ''}
                                placeholder="Rollnamn" sx={{ fontSize: '0.875rem' }} />
                            ) : (
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                {res.name || <Box component="span" sx={{ color: 'text.disabled' }}>(namnlös)</Box>}
                              </Typography>
                            )}
                            {res.shortName && (
                              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', letterSpacing: 1 }}>
                                {res.shortName}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <TextField variant="standard" size="small" type="number"
                          value={res.hourlyRate}
                          onChange={e => handleResourceChange(res.id, 'hourlyRate', e.target.value)}
                          inputProps={{ style: { textAlign: 'right' } }}
                          placeholder="0" sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{totalHrs.toFixed(1)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                          {fmtSEK(totalVal)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteResource(res.id)}
                          sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {budgetResources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        Inga resurser. Lägg till en person för att börja.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {budgetResources.length > 0 && (() => {
                  const grandHrs = budgetResources.reduce((s, res) =>
                    s + budgetActivities.reduce((as, act) =>
                      as + (act.resourceLines || [])
                        .filter(l => l.resourceId === res.id)
                        .reduce((ls, l) => ls + budgetMonths.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0), 0), 0), 0);
                  const grandVal = budgetResources.reduce((s, res) => {
                    const hrs = budgetActivities.reduce((as, act) =>
                      as + (act.resourceLines || [])
                        .filter(l => l.resourceId === res.id)
                        .reduce((ls, l) => ls + budgetMonths.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0), 0), 0);
                    return s + hrs * (parseFloat(res.hourlyRate) || 0);
                  }, 0);
                  return (
                    <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'yta.summa' } }}>
                      <TableCell colSpan={2}>Totalt</TableCell>
                      <TableCell align="right">{grandHrs.toFixed(1)} tim</TableCell>
                      <TableCell align="right" sx={{ color: '#FF6B00' }}>{fmtSEK(grandVal)}</TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </Paper>

          {/* Aktiviteter per månad */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight="bold">Aktiviteter per månad</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ToggleButtonGroup size="small" exclusive
                  value={resNameMode} onChange={(_, v) => v && setResNameMode(v)}
                  sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.7rem', lineHeight: 1.4 } }}>
                  <ToggleButton value="first">Förnamn</ToggleButton>
                  <ToggleButton value="full">Hela</ToggleButton>
                  <ToggleButton value="abbr" title="4-teckensförkortning">Förk.</ToggleButton>
                </ToggleButtonGroup>
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => handleAddActivity(budgetMonths)}
                  disabled={budgetMonths.length === 0}>
                  Lägg till aktivitet
                </Button>
              </Box>
            </Box>
            {budgetMonths.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                Ange period ovan för att börja lägga till aktiviteter.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'yta.rubrik' } }}>
                      <TableCell ref={actColRef} sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'yta.rubrik', p: 0,
                        ...(actColW !== null ? { width: actColW, minWidth: actColW, maxWidth: actColW } : { minWidth: 100 }) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
                          <span style={{ flex: 1 }}>Aktivitet</span>
                          <Box onMouseDown={e => startColResize('act', e)}
                            onDoubleClick={() => setActColW(null)}
                            title="Dra för att ändra bredd. Dubbelklicka för att återställa autobredd."
                            sx={{ width: 5, cursor: 'col-resize', alignSelf: 'stretch', flexShrink: 0,
                              borderRight: '2px solid', borderRightColor: 'divider', '&:hover': { borderRightColor: 'text.primary' } }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ position: 'sticky', left: actColW !== null ? actColW : actColRenderedW, zIndex: 2, bgcolor: 'yta.rubrik', width: resColW, minWidth: resColW, maxWidth: resColW, p: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 1 }}>
                          <span style={{ flex: 1 }}>Resurs</span>
                          <Box onMouseDown={e => startColResize('res', e)}
                            sx={{ width: 5, cursor: 'col-resize', alignSelf: 'stretch', flexShrink: 0,
                              borderRight: '2px solid', borderRightColor: 'divider', '&:hover': { borderRightColor: 'text.primary' } }} />
                        </Box>
                      </TableCell>
                      {budgetMonths.map((m, mi) => {
                        const isSrc = colCopyState?.sourceIdx === mi;
                        const isTgt = colCopyState && colCopyState.targetIdx === mi && colCopyState.targetIdx !== colCopyState.sourceIdx;
                        return (
                          <TableCell key={m} align="center"
                            onMouseEnter={() => onColHeaderEnter(mi)}
                            sx={{
                              width: 44, minWidth: 44, whiteSpace: 'nowrap', fontSize: '0.7rem', p: '4px 2px',
                              bgcolor: isSrc ? 'yta.kalla' : isTgt ? 'yta.mal' : 'yta.rubrik',
                            }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                              {fmtMonth(m)}
                              <Box
                                onMouseDown={e => startColCopyDrag(mi, budgetMonths, e)}
                                title="Dra för att kopiera kolumn"
                                sx={{
                                  width: 20, height: 4, borderRadius: 1,
                                  bgcolor: isSrc ? 'secondary.main' : 'action.disabled',
                                  cursor: 'ew-resize',
                                  '&:hover': { bgcolor: '#1565C0' },
                                }}
                              />
                            </Box>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Totalt tim</TableCell>
                      <TableCell align="right">Värde</TableCell>
                      <TableCell width={40} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {budgetActivities.map((act, actIdx) => {
                      const lines = act.resourceLines || [];
                      const groupBg = actIdx % 2 === 0 ? undefined : 'yta.rad';
                      return (
                        <React.Fragment key={act.id}>
                          {lines.map((line, lineIdx) => {
                            const lineHrs = budgetMonths.reduce((s, m) => s + (parseFloat(line.months?.[m]) || 0), 0);
                            const lineRes = budgetResources.find(r => r.id === line.resourceId);
                            const lineVal = lineHrs * (parseFloat(lineRes?.hourlyRate) || 0);
                            const isFirst = lineIdx === 0;
                            return (
                              <TableRow key={line.id} sx={{ bgcolor: groupBg }}>
                                <TableCell sx={{
                                  position: 'sticky', left: 0, zIndex: 1,
                                  bgcolor: groupBg || 'background.paper', overflow: 'hidden',
                                  borderLeft: !isFirst ? '3px solid #CE93D8' : undefined,
                                  ...(actColW !== null ? { width: actColW, minWidth: actColW, maxWidth: actColW } : { minWidth: 100 }),
                                }}>
                                  {isFirst ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <TextField variant="standard" size="small" value={act.name}
                                        onChange={e => handleActivityChange(act.id, 'name', e.target.value)}
                                        placeholder="Aktivitet" sx={{ flex: 1 }} />
                                      <IconButton size="small" title="Lägg till person"
                                        onClick={() => handleAddResourceLine(act.id, budgetMonths)}
                                        sx={{ opacity: 0.45, p: 0.25, '&:hover': { opacity: 1, color: '#7B1FA2' } }}>
                                        <AddIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ position: 'sticky', left: actColW !== null ? actColW : actColRenderedW, zIndex: 1, bgcolor: groupBg || 'background.paper', width: resColW, minWidth: resColW, maxWidth: resColW, overflow: 'hidden' }}>
                                  <FormControl variant="standard" size="small" sx={{ width: '100%' }}>
                                    <Select
                                      value={line.resourceId}
                                      onChange={e => handleResourceLineChange(act.id, line.id, 'resourceId', e.target.value)}
                                      displayEmpty
                                      renderValue={val => {
                                        if (!val) return <Box component="em" sx={{ color: 'text.disabled' }}>– Välj –</Box>;
                                        const r = budgetResources.find(r => r.id === val);
                                        return fmtResName(r, resNameMode);
                                      }}>
                                      <MenuItem value=""><em>– Välj –</em></MenuItem>
                                      {budgetResources.map(r => (
                                        <MenuItem key={r.id} value={r.id}>{r.name || '(namnlös)'}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                {budgetMonths.map((m, mi) => {
                                  const inFill = fillHighlight?.actId === act.id && fillHighlight?.lineId === line.id
                                    && mi >= fillHighlight.fromIdx && mi <= fillHighlight.toIdx;
                                  const inColSrc = colCopyState?.sourceIdx === mi;
                                  const inColTgt = colCopyState && colCopyState.targetIdx === mi && colCopyState.targetIdx !== colCopyState.sourceIdx;
                                  return (
                                    <TableCell key={m} align="center"
                                      onMouseEnter={() => { onFillCellEnter(act.id, line.id, mi); onColHeaderEnter(mi); }}
                                      sx={{
                                        p: '4px 2px', position: 'relative',
                                        bgcolor: inFill ? 'rgba(21,101,192,0.10)' : inColSrc ? 'rgba(255,107,0,0.07)' : inColTgt ? 'rgba(21,101,192,0.10)' : (groupBg || undefined),
                                        outline: inFill ? '1px dashed #1565C0' : undefined,
                                        '&:hover .cell-fill-handle': { opacity: 1 },
                                      }}>
                                      <TextField
                                        variant="standard"
                                        size="small"
                                        value={line.months?.[m] || ''}
                                        onChange={e => {
                                          const v = e.target.value.replace(/[^0-9.]/g, '');
                                          handleResourceLineMonthChange(act.id, line.id, m, v);
                                        }}
                                        inputProps={{ inputMode: 'decimal', style: { textAlign: 'center' } }}
                                        sx={{ width: 40 }}
                                      />
                                      <Box
                                        className="cell-fill-handle"
                                        onMouseDown={e => startFillDrag(act.id, line.id, mi, line.months?.[m] || 0, budgetMonths, e)}
                                        sx={{
                                          position: 'absolute', bottom: 2, right: 2,
                                          width: 7, height: 7,
                                          bgcolor: '#1565C0',
                                          cursor: 'crosshair',
                                          opacity: inFill ? 1 : 0,
                                          transition: 'opacity 0.1s',
                                          userSelect: 'none',
                                        }}
                                      />
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right">
                                  <Typography variant="body2">{lineHrs > 0 ? lineHrs.toFixed(1) : ''}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                                    {lineVal > 0 ? fmtSEK(lineVal) : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => handleDeleteResourceLine(act.id, line.id)}
                                    sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                    {budgetActivities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={budgetMonths.length + 5} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            Inga aktiviteter ännu. Lägg till en aktivitet för att börja.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {budgetActivities.length > 0 && (() => {
                      const totalAllHrs = budgetActivities.reduce((s, act) =>
                        s + (act.resourceLines || []).reduce((ls, l) =>
                          ls + budgetMonths.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0), 0), 0);
                      const totalAllVal = budgetActivities.reduce((s, act) =>
                        s + (act.resourceLines || []).reduce((ls, l) => {
                          const res = budgetResources.find(r => r.id === l.resourceId);
                          const hrs = budgetMonths.reduce((ms, m) => ms + (parseFloat(l.months?.[m]) || 0), 0);
                          return ls + hrs * (parseFloat(res?.hourlyRate) || 0);
                        }, 0), 0);
                      return (
                        <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'yta.summa' } }}>
                          <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'yta.summa',
                            ...(actColW !== null ? { width: actColW, minWidth: actColW } : { minWidth: 100 }) }}>
                            Totalt
                          </TableCell>
                          <TableCell sx={{ position: 'sticky', left: actColW !== null ? actColW : actColRenderedW, zIndex: 1, bgcolor: 'yta.summa', width: resColW, minWidth: resColW }} />
                          {budgetMonths.map(m => {
                            const colSum = budgetActivities.reduce((s, act) =>
                              s + (act.resourceLines || []).reduce((ls, l) => ls + (parseFloat(l.months?.[m]) || 0), 0), 0);
                            return (
                              <TableCell key={m} align="center">
                                {colSum > 0 ? colSum.toFixed(1) : ''}
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">{totalAllHrs.toFixed(1)}</TableCell>
                          <TableCell align="right" sx={{ color: '#FF6B00' }}>{fmtSEK(totalAllVal)}</TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          {/* Licenskostnader */}
          {activeBudget && (
          <Paper sx={{ mt: 2, p: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold">Licenskostnader</Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={handleAddLicense}>
                Lägg till licens
              </Button>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'yta.rubrik', fontWeight: 600, fontSize: '0.75rem' } }}>
                    <TableCell sx={{ width: 28, bgcolor: 'yta.rubrik' }} />
                    <TableCell>Plattform / system</TableCell>
                    <TableCell sx={{ width: 160 }}>Pristyp</TableCell>
                    <TableCell sx={{ width: 70 }} align="center">Antal</TableCell>
                    <TableCell sx={{ width: 130 }} align="right">Pris (kr)</TableCell>
                    <TableCell sx={{ width: 130 }} align="right">Budgeterat totalt</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetLicenses.map(lic => (
                    <TableRow key={lic.id} draggable
                      onDragStart={() => { dragLicRef.current = lic.id; }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => {
                        const fromId = dragLicRef.current;
                        if (!fromId || fromId === lic.id) return;
                        updateActiveBudget(b => {
                          const lics = [...(b.licenses || [])];
                          const fi = lics.findIndex(l => l.id === fromId);
                          const ti = lics.findIndex(l => l.id === lic.id);
                          const [moved] = lics.splice(fi, 1);
                          lics.splice(ti, 0, moved);
                          return { licenses: lics };
                        });
                        dragLicRef.current = null;
                      }}>
                      <TableCell sx={{ cursor: 'grab', color: 'text.disabled', px: 0.5 }}>
                        <DragIndicatorIcon sx={{ fontSize: 16, display: 'block' }} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" size="small" value={lic.name}
                          onChange={e => handleLicenseChange(lic.id, 'name', e.target.value)}
                          error={ofullstandiga.licenses.includes(lic.id)}
                          helperText={ofullstandiga.licenses.includes(lic.id) ? 'Sparas när plattformen är ifylld' : ''}
                          placeholder="t.ex. Autodesk BIM 360" sx={{ width: '100%' }} />
                      </TableCell>
                      <TableCell>
                        <FormControl variant="standard" size="small" sx={{ width: '100%' }}>
                          <Select value={lic.priceType}
                            onChange={e => handleLicenseChange(lic.id, 'priceType', e.target.value)}>
                            <MenuItem value="fixed">Fast (hela perioden)</MenuItem>
                            <MenuItem value="monthly">Per månad</MenuItem>
                            <MenuItem value="quarterly">Per kvartal</MenuItem>
                            <MenuItem value="yearly">Per år</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        {lic.priceType !== 'fixed' && (
                          <TextField variant="standard" size="small"
                            value={lic.quantity ?? 1}
                            onChange={e => handleLicenseChange(lic.id, 'quantity', e.target.value.replace(/[^0-9]/g, '') || '1')}
                            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
                            sx={{ width: 48 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField variant="standard" size="small" value={lic.price}
                          onChange={e => handleLicenseChange(lic.id, 'price', e.target.value.replace(/[^0-9.]/g, ''))}
                          inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                          sx={{ width: 100 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                          {fmtSEK(licenseBudgetTotal(lic))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDeleteLicense(lic.id)}
                          sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {budgetLicenses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Inga licenskostnader. Klicka "Lägg till" för att lägga till en digital plattform.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {budgetLicenses.length > 0 && (
                    <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'yta.summa' } }}>
                      <TableCell colSpan={5}>Totalt licenskostnader</TableCell>
                      <TableCell align="right" sx={{ color: '#FF6B00' }}>
                        {fmtSEK(budgetLicenses.reduce((s, l) => s + licenseBudgetTotal(l), 0))}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
          )}

          {/* Belastningsdiagram */}
          {activeBudget && budgetMonths.length > 1 && (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              Belastning över tid
            </Typography>
            <BudgetChart
              months={budgetMonths}
              resources={budgetResources}
              activities={budgetActivities}
              licenses={budgetLicenses}
              height={260}
            />
          </Paper>
          )}

          {/* Utfall per resurs och månad */}
          {activeBudget && budgetResources.length > 0 && budgetMonths.length > 0 && (
          <Paper sx={{ mt: 2, overflow: 'hidden' }}>
            <Box sx={{
              bgcolor: '#4A148C', color: 'white', px: 2.5, py: 1.5,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Typography fontWeight="bold" sx={{ letterSpacing: 0.5 }}>Utfall per resurs och månad</Typography>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.75rem',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Importera Milltime
                <input type="file" hidden accept=".xls,.xlsx" onChange={handleUtfallImport} />
              </Button>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: 'yta.lila',
                      position: 'sticky', left: 0, zIndex: 2, minWidth: 150 }}>
                      Resurs
                    </TableCell>
                    {budgetMonths.map(m => (
                      <TableCell key={m} align="center"
                        sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: 'yta.lila',
                          width: 44, minWidth: 44, whiteSpace: 'nowrap', p: '4px 2px' }}>
                        {fmtMonth(m)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: 'yta.lila', whiteSpace: 'nowrap' }}>
                      Totalt tim
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetResources.map(res => {
                    const utfall = activeBudget?.utfall?.[res.id] || {};
                    const totalU = budgetMonths.reduce((s, m) => s + (parseFloat(utfall[m]) || 0), 0);
                    return (
                      <TableRow key={res.id}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', minWidth: 150 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {res.isRole && (
                              <Tooltip title="Rollbaserad resurs — Milltime-utfall kan inte kopplas automatiskt. Ersätt med en namngiven person för att aktivera uppföljning." arrow>
                                <WarningAmberIcon sx={{ fontSize: 15, color: '#E65100', flexShrink: 0 }} />
                              </Tooltip>
                            )}
                            <Typography sx={{ fontSize: '0.875rem' }}>{fmtResName(res, resNameMode)}</Typography>
                          </Box>
                        </TableCell>
                        {budgetMonths.map(m => (
                          <TableCell key={m} align="center" sx={{ p: '4px 2px' }}>
                            {res.isRole ? (
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>–</Typography>
                            ) : (
                              <TextField
                                variant="standard" size="small"
                                value={utfall[m] || ''}
                                onChange={e => {
                                  const v = e.target.value.replace(/[^0-9.]/g, '');
                                  updateActiveBudget(b => ({
                                    utfall: {
                                      ...(b.utfall || {}),
                                      [res.id]: { ...(b.utfall?.[res.id] || {}), [m]: parseFloat(v) || 0 },
                                    },
                                  }));
                                }}
                                inputProps={{ inputMode: 'decimal', style: { textAlign: 'center' } }}
                                sx={{ width: 40 }}
                              />
                            )}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <Typography sx={{ fontSize: '0.8rem', color: totalU > 0 ? 'yta.lilaText' : 'text.disabled',
                            fontWeight: totalU > 0 ? 700 : 400 }}>
                            {res.isRole ? <span style={{ color: '#E65100', fontSize: '0.7rem' }}>Roll</span> : (totalU > 0 ? totalU.toFixed(1) : '–')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Summarad */}
                  {(() => {
                    const grandTotal = budgetResources.reduce((s, res) => {
                      const u = activeBudget?.utfall?.[res.id] || {};
                      return s + budgetMonths.reduce((ms, m) => ms + (parseFloat(u[m]) || 0), 0);
                    }, 0);
                    return (
                      <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'yta.lila' } }}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'yta.lila' }}>Totalt</TableCell>
                        {budgetMonths.map(m => {
                          const colSum = budgetResources.reduce((s, res) => {
                            const u = activeBudget?.utfall?.[res.id] || {};
                            return s + (parseFloat(u[m]) || 0);
                          }, 0);
                          return (
                            <TableCell key={m} align="center"
                              sx={{ color: colSum > 0 ? 'yta.lilaText' : 'text.disabled', fontSize: '0.8rem' }}>
                              {colSum > 0 ? colSum.toFixed(1) : ''}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ color: grandTotal > 0 ? 'yta.lilaText' : 'text.disabled' }}>
                          {grandTotal > 0 ? grandTotal.toFixed(1) : '–'}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </Box>
          </Paper>
          )}

          {/* ── Jämförelse: Budget vs. utfall per månad ── */}
          {activeBudget && budgetResources.length > 0 && budgetMonths.length > 0 && (
          <Paper sx={{ mt: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderBottomColor: 'divider',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">Budget vs. utfall per månad</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {[
                  { bg: 'status.underBg', fg: 'status.underText', label: 'Under (< 80 %)' },
                  { bg: 'status.naraBg', fg: 'status.naraText', label: 'Nära (80–100 %)' },
                  { bg: 'status.overBg', fg: 'status.overText', label: 'Över (> 100 %)' },
                ].map(({ bg, fg, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 11, height: 11, borderRadius: 0.3, bgcolor: bg, border: `1px solid ${fg}`, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
                <ToggleButtonGroup size="small" exclusive
                  value={compMode} onChange={(_, v) => v && setCompMode(v)}
                  sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.7rem' } }}>
                  <ToggleButton value="hrs">Tim</ToggleButton>
                  <ToggleButton value="sek">SEK</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'yta.rubrik', fontSize: '0.75rem' } }}>
                    <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'yta.rubrik', minWidth: 150 }}>Resurs</TableCell>
                    {budgetMonths.map(m => (
                      <TableCell key={m} align="center" sx={{ minWidth: 80, whiteSpace: 'nowrap', p: '6px 4px' }}>
                        {fmtMonth(m)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Totalt budget</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Totalt utfall</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Skillnad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {budgetResources.map(res => {
                    const rate = parseFloat(res.hourlyRate) || 0;
                    const utfall = activeBudget?.utfall?.[res.id] || {};
                    return (
                      <TableRow key={res.id}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', minWidth: 150 }}>
                          <Typography sx={{ fontSize: '0.875rem' }}>{fmtResName(res, resNameMode)}</Typography>
                        </TableCell>
                        {budgetMonths.map(m => {
                          const bHrs = budgHrsByResMonth[res.id]?.[m] || 0;
                          const uHrs = parseFloat(utfall[m]) || 0;
                          const bVal = compMode === 'sek' ? bHrs * rate : bHrs;
                          const uVal = compMode === 'sek' ? uHrs * rate : uHrs;
                          const { bg, fg } = compCellStyle(bVal, uVal);
                          return (
                            <TableCell key={m} align="center" sx={{ p: '4px 6px', bgcolor: bg || undefined, minWidth: 80 }}>
                              {(bVal > 0 || uVal > 0) ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', lineHeight: 1.2 }}>
                                    {compMode === 'sek' ? (bVal > 0 ? `${Math.round(bVal / 1000)}k` : '–') : (bVal > 0 ? bVal.toFixed(1) : '–')}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: fg || 'text.primary', lineHeight: 1.3 }}>
                                    {compMode === 'sek' ? (uVal > 0 ? `${Math.round(uVal / 1000)}k` : '–') : (uVal > 0 ? uVal.toFixed(1) : '–')}
                                  </Typography>
                                </Box>
                              ) : null}
                            </TableCell>
                          );
                        })}
                        {(() => {
                          const rb = budgetMonths.reduce((s, m) => s + (compMode === 'sek' ? (budgHrsByResMonth[res.id]?.[m] || 0) * rate : (budgHrsByResMonth[res.id]?.[m] || 0)), 0);
                          const ru = budgetMonths.reduce((s, m) => s + (compMode === 'sek' ? (parseFloat(utfall[m]) || 0) * rate : (parseFloat(utfall[m]) || 0)), 0);
                          const diff = ru - rb;
                          const { fg: diffFg } = compCellStyle(rb, ru);
                          const fmt = v => compMode === 'sek' ? (v !== 0 ? Number(v).toLocaleString('sv-SE') + ' kr' : '–') : (v !== 0 ? v.toFixed(1) : '–');
                          return (<>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{fmt(rb)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, color: diffFg || 'text.primary' }}>{fmt(ru)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700,
                              color: diff > 0 ? 'error.main' : diff < 0 ? 'success.main' : 'text.secondary' }}>
                              {(rb > 0 || ru > 0) ? (diff >= 0 ? '+' : '') + (compMode === 'sek' ? Number(diff).toLocaleString('sv-SE') + ' kr' : diff.toFixed(1)) : '–'}
                            </TableCell>
                          </>);
                        })()}
                      </TableRow>
                    );
                  })}
                  {(() => {
                    const grandBudg = budgetMonths.reduce((s, m) => s + budgetResources.reduce((rs, res) => rs + (compMode === 'sek' ? (budgHrsByResMonth[res.id]?.[m] || 0) * (parseFloat(res.hourlyRate) || 0) : (budgHrsByResMonth[res.id]?.[m] || 0)), 0), 0);
                    const grandUtf  = budgetMonths.reduce((s, m) => s + budgetResources.reduce((rs, res) => rs + (compMode === 'sek' ? (parseFloat(activeBudget?.utfall?.[res.id]?.[m]) || 0) * (parseFloat(res.hourlyRate) || 0) : (parseFloat(activeBudget?.utfall?.[res.id]?.[m]) || 0)), 0), 0);
                    const grandDiff = grandUtf - grandBudg;
                    const { fg: gFg } = compCellStyle(grandBudg, grandUtf);
                    const fmt = v => compMode === 'sek' ? Number(v).toLocaleString('sv-SE') + ' kr' : v.toFixed(1);
                    return (
                      <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'yta.summa', borderTop: '2px solid', borderTopColor: 'divider' } }}>
                        <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'yta.summa' }}>Totalt</TableCell>
                        {budgetMonths.map(m => {
                          const bV = budgetResources.reduce((s, res) => s + (compMode === 'sek' ? (budgHrsByResMonth[res.id]?.[m] || 0) * (parseFloat(res.hourlyRate) || 0) : (budgHrsByResMonth[res.id]?.[m] || 0)), 0);
                          const uV = budgetResources.reduce((s, res) => s + (compMode === 'sek' ? (parseFloat(activeBudget?.utfall?.[res.id]?.[m]) || 0) * (parseFloat(res.hourlyRate) || 0) : (parseFloat(activeBudget?.utfall?.[res.id]?.[m]) || 0)), 0);
                          const { bg } = compCellStyle(bV, uV);
                          return (
                            <TableCell key={m} align="center" sx={{ p: '4px 6px', bgcolor: bg || 'yta.summa', minWidth: 80 }}>
                              {(bV > 0 || uV > 0) ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', lineHeight: 1.2 }}>
                                    {compMode === 'sek' ? (bV > 0 ? `${Math.round(bV / 1000)}k` : '–') : (bV > 0 ? bV.toFixed(1) : '–')}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.3 }}>
                                    {compMode === 'sek' ? (uV > 0 ? `${Math.round(uV / 1000)}k` : '–') : (uV > 0 ? uV.toFixed(1) : '–')}
                                  </Typography>
                                </Box>
                              ) : null}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">{fmt(grandBudg)}</TableCell>
                        <TableCell align="right" sx={{ color: gFg || 'inherit' }}>{fmt(grandUtf)}</TableCell>
                        <TableCell align="right" sx={{ color: grandDiff > 0 ? 'error.main' : grandDiff < 0 ? 'success.main' : 'inherit' }}>
                          {(grandDiff >= 0 ? '+' : '') + fmt(grandDiff)}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </Box>
          </Paper>
          )}

          {/* ── Insikter ── */}
          {activeBudget && budgetResources.length > 0 && budgetMonths.length > 0 && (() => {
            const totalBudgHrs = budgetResources.reduce((s, res) =>
              s + budgetMonths.reduce((ms, m) => ms + (budgHrsByResMonth[res.id]?.[m] || 0), 0), 0);
            const totalUtfHrs = budgetResources.reduce((s, res) => {
              const u = activeBudget?.utfall?.[res.id] || {};
              return s + budgetMonths.reduce((ms, m) => ms + (parseFloat(u[m]) || 0), 0);
            }, 0);
            if (totalBudgHrs === 0) return null;
            const consumedPct = Math.round((totalUtfHrs / totalBudgHrs) * 100);
            const flagThr = activeBudget.flagThreshold ?? 70;
            // Time elapsed
            let timePct = null;
            if (activeBudget.startMonth && activeBudget.endMonth) {
              const [sy, sm] = activeBudget.startMonth.split('-').map(Number);
              const [ey, em] = activeBudget.endMonth.split('-').map(Number);
              const totalMonths = (ey - sy) * 12 + (em - sm) + 1;
              const now = new Date();
              const elapsed = Math.max(0, (now.getFullYear() - sy) * 12 + (now.getMonth() + 1 - sm) + 1);
              timePct = totalMonths > 0 ? Math.round(Math.min(100, (elapsed / totalMonths) * 100)) : null;
            }
            // Insight severity
            let sev, headline, body;
            if (totalUtfHrs === 0) {
              sev = 'info'; headline = 'Ingen utfallsdata ännu';
              body = `Totalt budgeterat: ${totalBudgHrs.toFixed(0)} tim. Registrera utfall ovan för att se jämförelse.`;
            } else if (consumedPct > 100) {
              sev = 'error'; headline = `Budgeten är överskriden med ${(totalUtfHrs - totalBudgHrs).toFixed(0)} tim`;
              body = `Utfall ${totalUtfHrs.toFixed(0)} tim vs budget ${totalBudgHrs.toFixed(0)} tim (${consumedPct}%). Åtgärd rekommenderas.`;
            } else if (consumedPct >= flagThr) {
              sev = 'warning'; headline = `ABK09-varning: ${consumedPct}% av budgeten är förbrukad`;
              body = `Enligt ABK09 ska kunden informeras när ${flagThr}% av budgeten är nådd.${timePct !== null && timePct < flagThr ? ` Notering: ${timePct}% av perioden har förflutit — budgeten förbrukas snabbare än planerat.` : ''}`;
            } else {
              sev = 'success'; headline = `Under kontroll – ${consumedPct}% av budgeten förbrukad`;
              body = `${totalUtfHrs.toFixed(0)} av ${totalBudgHrs.toFixed(0)} tim.${timePct !== null ? ` ${timePct}% av projekttiden har förflutit.` : ''}`;
            }
            const sc = { success: { bg:'status.underBg', border:'#4CAF50', text:'status.underText', icon:'✓' }, warning: { bg:'status.naraBg', border:'#FF9800', text:'status.naraText', icon:'⚠' }, error: { bg:'status.overBg', border:'#EF5350', text:'status.overText', icon:'!' }, info: { bg:'status.infoBg', border:'#2196F3', text:'status.infoText', icon:'i' } }[sev];
            return (
              <Paper sx={{ mt: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderBottomColor: 'divider',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Insikter</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Flagga vid:</Typography>
                    <TextField size="small" type="number"
                      value={flagThr}
                      onChange={e => {
                        const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 70));
                        updateActiveBudget(() => ({ flagThreshold: v }));
                      }}
                      inputProps={{ min: 1, max: 100, style: { textAlign: 'center', width: 44 } }}
                      sx={{ width: 72 }} />
                    <Typography variant="caption" color="text.secondary">%</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  {/* Alert box */}
                  <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: sc.bg, border: `1.5px solid ${sc.border}`,
                    display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: sc.border, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 900, fontSize: '0.85rem' }}>
                      {sc.icon}
                    </Box>
                    <Box>
                      <Typography fontWeight="bold" sx={{ color: sc.text, mb: 0.3 }}>{headline}</Typography>
                      <Typography variant="body2" color="text.secondary">{body}</Typography>
                    </Box>
                  </Box>
                  {/* Progress bars */}
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Budgetförbrukning
                      </Typography>
                      <Box sx={{ position: 'relative', height: 10, bgcolor: 'yta.spar', borderRadius: 5, mb: 0.5 }}>
                        <Box sx={{ height: '100%', borderRadius: 5, bgcolor: sc.border,
                          width: `${Math.min(consumedPct, 100)}%`, transition: 'width 0.4s' }} />
                        <Box sx={{ position: 'absolute', top: -3, left: `${flagThr}%`,
                          width: 2, height: 16, bgcolor: 'text.secondary', borderRadius: 1 }}
                          title={`Flaggningströskel: ${flagThr}%`} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.disabled">0%</Typography>
                        <Typography variant="caption" sx={{ color: sc.text, fontWeight: 700 }}>{consumedPct}%</Typography>
                        <Typography variant="caption" color="text.disabled">100%</Typography>
                      </Box>
                    </Box>
                    {timePct !== null && (
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Projekttid förfluten
                        </Typography>
                        <Box sx={{ height: 10, bgcolor: 'yta.spar', borderRadius: 5, mb: 0.5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', borderRadius: 5, bgcolor: '#90A4AE',
                            width: `${timePct}%`, transition: 'width 0.4s' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.disabled">{activeBudget.startMonth}</Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>{timePct}% av perioden</Typography>
                          <Typography variant="caption" color="text.disabled">{activeBudget.endMonth}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })()}

          </>)}
          </Box>
        </TabPanel>

        {/* ── Tab 2: ExternTid ── */}
        <TabPanel value={tab} index={2}>
          <ExternTidTab
            projectId={projectId}
            currentUser={currentUser}
            userProfile={userProfile}
          />
        </TabPanel>
      </Container>

      {/* Skapa referensprojekt dialog */}
      <Dialog open={createRefDialog} onClose={() => setCreateRefDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#FF6B00' }} />
            <Typography fontWeight="bold">Skapa referensprojekt</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <TextField fullWidth size="small" label="Uppdragsnamn *"
              value={createRefForm.name || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, name: e.target.value }))} />
            <TextField fullWidth size="small" label="Vår roll"
              value={createRefForm.role || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, role: e.target.value }))} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Startdatum" placeholder="YYYY-MM"
                  value={createRefForm.startDate || ''}
                  onChange={e => setCreateRefForm(f => ({ ...f, startDate: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Slutdatum" placeholder="YYYY-MM"
                  value={createRefForm.endDate || ''}
                  onChange={e => setCreateRefForm(f => ({ ...f, endDate: e.target.value }))} />
              </Grid>
            </Grid>
            <TextField fullWidth size="small" label="Budget / Kontraktsvärde"
              value={createRefForm.budget || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, budget: e.target.value }))} />
            <TextField fullWidth size="small" label="Uppdragsbeskrivning" multiline rows={3}
              value={createRefForm.projectDescription || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, projectDescription: e.target.value }))} />
            <TextField fullWidth size="small" label="Vår uppgift" multiline rows={2}
              value={createRefForm.ourAssignment || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, ourAssignment: e.target.value }))} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Nyckelord
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                {(createRefForm.keywords || []).map((k, i) => (
                  <Chip key={i} label={k} size="small" onDelete={() =>
                    setCreateRefForm(f => ({ ...f, keywords: f.keywords.filter((_, j) => j !== i) }))} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Lägg till nyckelord"
                  value={refTagInput} onChange={e => setRefTagInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && refTagInput.trim()) {
                      setCreateRefForm(f => ({ ...f, keywords: [...(f.keywords || []), refTagInput.trim()] }));
                      setRefTagInput('');
                    }
                  }} sx={{ flex: 1 }} />
                <Button size="small" onClick={() => {
                  if (refTagInput.trim()) {
                    setCreateRefForm(f => ({ ...f, keywords: [...(f.keywords || []), refTagInput.trim()] }));
                    setRefTagInput('');
                  }
                }}>Lägg till</Button>
              </Box>
            </Box>
            <TextField fullWidth size="small" label="Affärsområde"
              select value={createRefForm.departmentId || ''}
              onChange={e => setCreateRefForm(f => ({ ...f, departmentId: e.target.value }))}>
              <MenuItem value="">– Välj affärsområde –</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>

            {/* Bild */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Projektbild
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {createRefForm.imageUrl ? (
                  <Box component="img" src={createRefForm.imageUrl} alt="Projektbild"
                    sx={{ width: 100, height: 70, objectFit: 'cover',
                      borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                ) : (
                  <Box sx={{ width: 100, height: 70, bgcolor: 'action.hover', borderRadius: 1,
                    border: '2px dashed', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      Ingen bild
                    </Typography>
                  </Box>
                )}
                <Box>
                  {project.imageUrl && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Hämtad från uppdraget
                    </Typography>
                  )}
                  <TextField size="small" label="Bild-URL" fullWidth
                    value={createRefForm.imageUrl || ''}
                    onChange={e => setCreateRefForm(f => ({ ...f, imageUrl: e.target.value }))}
                    placeholder="https://..." sx={{ minWidth: 260 }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateRefDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateRef}
            disabled={savingRef || !createRefForm.name?.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {savingRef ? 'Sparar...' : 'Skapa referensprojekt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statusändring dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Bekräfta statusändring</DialogTitle>
        <DialogContent>
          <Typography>
            Vill du ändra status till{' '}
            <strong>{STATUS_META[pendingStatus]?.label}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={() => handleStatusChange(pendingStatus)}>
            Bekräfta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bekräfta radera budget */}
      <Dialog open={!!confirmDeleteBudgetId} onClose={() => setConfirmDeleteBudgetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Radera budget?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDeleteBudgetId && budgets.find(b => b.id === confirmDeleteBudgetId)?.name} raderas permanent. Det går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteBudgetId(null)}>Avbryt</Button>
          <Button variant="contained" onClick={confirmDeleteBudget}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}>
            Radera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excelimport ersätter innehåll – bekräftas först */}
      <Dialog open={!!importForslag} onClose={() => setImportForslag(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Importera {importForslag?.fil}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Importen ersätter innehåll i {activeBudget?.name || 'budgeten'} och sparas direkt:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {(importForslag?.ersatter || []).map(t => (
              <Typography component="li" variant="body2" key={t}>{t}</Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Vill du behålla den nuvarande budgeten: avbryt, duplicera den och importera till kopian.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportForslag(null)}>Avbryt</Button>
          <Button variant="contained" onClick={() => {
            const { patch } = importForslag;
            setImportForslag(null);
            andraStruktur();
            updateActiveBudget(() => patch);
          }}>
            Importera och ersätt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Borttagning av rad med timmar eller belopp */}
      <Dialog open={!!bekraftaBort} onClose={() => setBekraftaBort(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ta bort raden?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{bekraftaBort?.text}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Ändringen sparas direkt och går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBekraftaBort(null)}>Avbryt</Button>
          <Button variant="contained" color="error" onClick={() => {
            const { gor } = bekraftaBort;
            setBekraftaBort(null);
            gor();
          }}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kopiera/flytta budget till annat projekt */}
      <Dialog open={copyDialog} onClose={() => !copyBusy && setCopyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Kopiera eller flytta budget</DialogTitle>
        <DialogContent>
          {copyDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>{copyDone}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Budgeten <strong>{activeBudget?.name}</strong> överförs till valt projekt
                med alla resurser, aktiviteter och licenser.
              </Typography>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={copyMode}
                onChange={(e, v) => v && setCopyMode(v)}
                fullWidth
              >
                <ToggleButton value="copy">
                  <ContentCopyIcon sx={{ fontSize: 16, mr: 0.5 }} /> Kopiera
                </ToggleButton>
                <ToggleButton value="move">
                  <DriveFileMoveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Flytta
                </ToggleButton>
              </ToggleButtonGroup>

              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                {copyMode === 'move'
                  ? 'Budgeten tas bort från det här projektet efter flytten.'
                  : 'Budgeten finns kvar i det här projektet.'}
              </Typography>

              <Autocomplete
                options={copyProjectOptions}
                value={copyTarget}
                onChange={(e, v) => setCopyTarget(v)}
                getOptionLabel={o => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={params => (
                  <TextField {...params} label="Målprojekt" size="small" autoFocus />
                )}
                noOptionsText="Inga andra projekt"
              />

              {copyError && <Alert severity="error">{copyError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {copyDone ? (
            <Button variant="contained" onClick={() => setCopyDialog(false)}>
              Stäng
            </Button>
          ) : (
            <>
              <Button onClick={() => setCopyDialog(false)} disabled={copyBusy}>Avbryt</Button>
              <Button variant="contained" onClick={handleCopyBudgetToProject}
                disabled={copyBusy || !copyTarget}
                startIcon={copyBusy ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                {copyBusy ? 'Överför...' : (copyMode === 'move' ? 'Flytta' : 'Kopiera')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
